/**
 * Publishing Channels API bindings — Patch 33.2.
 * Frontend never sees "Zernio" — provider-neutral surface only.
 * Uses the workspace-scoped `/api/v1/*` prefix; the shared `http`
 * function prefixes `/api/v1` automatically.
 */
import { http } from "@/shared/api/http";

export type ChannelStatus = "connected" | "disconnected" | "revoked" | "error" | "over_limit";

export interface ConnectedChannel {
  id: string;
  workspace_id: string;
  provider: string;
  provider_account_id: string;
  platform: string;
  display_name: string | null;
  username: string | null;
  external_url: string | null;
  status: ChannelStatus;
  is_connected: boolean;
  connected_at: string | null;
  disconnected_at: string | null;
  metadata: Record<string, unknown>;
}

export interface ChannelLimit {
  allowed: boolean;
  limit: number | null;
  used: number;
  remaining: number | null;
  reason: string;
}

export interface ChannelsResponse {
  provider: string;
  provider_ready: boolean;
  channels: ConnectedChannel[];
  limit: ChannelLimit;
}

export interface ScoreboardResponse {
  connected_channels: number;
  by_status: Record<string, number>;
  by_platform: Record<string, number>;
  recent: Array<{ job_id: string; status: string; channel: string; updated_at: string; asset_id: string }>;
  metrics_available: boolean;
}

export interface ConnectStartResponse {
  ok: boolean;
  url?: string;
  state?: string;
  reason?: string;
  limit?: number | null;
  used?: number;
}

const base = (workspaceId: string) => `/workspaces/${workspaceId}/publishing`;

export interface PublishPayload {
  provider_account_ids: string[];
  body: string;
  media?: Array<{ url: string; kind?: string; alt_text?: string | null }>;
  scheduled_for?: string | null;
  publish_now?: boolean;
  timezone?: string;
  idempotency_key: string;
  metadata?: Record<string, unknown>;
}

export interface PublishResult {
  provider: string;
  status: string;
  provider_post_id: string | null;
  external_url: string | null;
  per_account_results: Array<{
    provider_account_id: string;
    platform: string;
    status: string;
    provider_post_id: string | null;
    external_url: string | null;
    error_code: string | null;
    error_message: string | null;
  }>;
  latency_ms: number;
}

export interface HistoryFilter {
  status?: string;
  platform?: string;
  since?: string;
  limit?: number;
}

export interface HistoryRow {
  id: string;
  workspace_id: string;
  asset_id: string | null;
  provider: string;
  provider_post_id: string | null;
  external_url: string | null;
  status: string;
  channel: string | null;
  content_preview: string | null;
  scheduled_for: string | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  per_account_results: PublishResult["per_account_results"];
}

export const PublishingChannelsAPI = {
  listChannels: (workspaceId: string): Promise<ChannelsResponse> =>
    http(`${base(workspaceId)}/channels`),
  scoreboard: (workspaceId: string): Promise<ScoreboardResponse> =>
    http(`${base(workspaceId)}/scoreboard`),
  history: (workspaceId: string, filter: HistoryFilter = {}): Promise<{ items: HistoryRow[]; total: number }> => {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.platform) q.set("platform", filter.platform);
    if (filter.since) q.set("since", filter.since);
    if (filter.limit) q.set("limit", String(filter.limit));
    const qs = q.toString();
    return http(`${base(workspaceId)}/history${qs ? `?${qs}` : ""}`);
  },
  ensureProfile: (
    workspaceId: string,
    displayName?: string,
  ): Promise<{ provider: string; external_profile_id: string }> =>
    http(`${base(workspaceId)}/profile`, {
      method: "POST",
      body: { display_name: displayName ?? null },
    }),
  startConnect: (
    workspaceId: string,
    platform: string,
    redirectUri: string,
  ): Promise<ConnectStartResponse> =>
    http(`${base(workspaceId)}/connect/${platform}`, {
      method: "POST",
      body: { platform, redirect_uri: redirectUri },
    }),
  syncChannels: (
    workspaceId: string,
  ): Promise<{ synced_count: number; channels: ConnectedChannel[] }> =>
    http(`${base(workspaceId)}/sync`, { method: "POST", body: {} }),
  disconnect: (workspaceId: string, connectionId: string): Promise<{ ok: boolean }> =>
    http(`${base(workspaceId)}/channels/${connectionId}`, { method: "DELETE" }),
  publish: (workspaceId: string, payload: PublishPayload): Promise<PublishResult> =>
    http(`${base(workspaceId)}/posts`, { method: "POST", body: payload }),
};

export const LAUNCH_PLATFORMS = [
  { key: "facebook", label: "Facebook", icon: "facebook", color: "#1877F2" },
  { key: "instagram", label: "Instagram", icon: "instagram", color: "#E1306C" },
  { key: "google_business", label: "Google Business Profile", icon: "map-pin", color: "#4285F4" },
  { key: "linkedin", label: "LinkedIn", icon: "linkedin", color: "#0A66C2" },
  { key: "tiktok", label: "TikTok", icon: "music", color: "#00F2EA" },
] as const;

export type LaunchPlatformKey = (typeof LAUNCH_PLATFORMS)[number]["key"];
